import { createContext, useContext, useEffect, useState, type ImgHTMLAttributes } from 'react';
import { apiClient } from '@indipia/shared';

/**
 * 발송자 미리보기 모드 — 고객대시보드를 로그인한 사업장 회원이 열었을 때 true.
 * 미리보기에서는 방문 기록·읽음 처리·사진 저장(zip·개별)을 하지 않고, 이미지는 로그인 토큰으로 받는다.
 */
export const PreviewContext = createContext(false);
export const useIsPreview = () => useContext(PreviewContext);

/** 로그인 토큰(Authorization)으로 이미지를 blob 으로 받아 object URL 을 돌려준다. 로딩 중 undefined, 실패 null. */
function useAuthedBlobUrl(url: string | null): string | null | undefined {
  const [state, setState] = useState<{ url: string; blob: string | null } | null>(null);
  useEffect(() => {
    if (!url) return;
    const controller = new AbortController();
    let objectUrl: string | null = null;
    apiClient
      .get<Blob>(url, { responseType: 'blob', signal: controller.signal })
      .then((res) => {
        objectUrl = URL.createObjectURL(res.data);
        setState({ url, blob: objectUrl });
      })
      .catch(() => {
        if (!controller.signal.aborted) setState({ url, blob: null });
      });
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);
  if (!url) return undefined;
  return state && state.url === url ? state.blob : undefined;
}

/**
 * 고객대시보드 이미지 — 공유 쿠키 고객은 <img src> 요청에 쿠키가 자동으로 실려 그대로 쓴다.
 * 미리보기(로그인 토큰)는 <img> 가 Authorization 헤더를 못 실어 blob 으로 받아 그린다.
 * 사진 서비스가 아닌 곳에서 내려주는 이미지(현장리포트 스냅샷)는 공유 범위 쿠키가 필요해 미리보기에서는 열리지 않을 수 있다.
 */
export function CustomerImg({ src, ...rest }: Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> & { src: string }) {
  const preview = useIsPreview();
  const blobUrl = useAuthedBlobUrl(preview ? src : null);
  if (!preview) return <img src={src} {...rest} />;
  if (blobUrl === undefined) return null; // 받는 중
  return <img src={blobUrl ?? src} {...rest} />; // 실패 시 원 주소로 폴백(깨진 이미지 표시 대신 onError 흐름 유지)
}
